import type { Platform } from '../../types';

interface MediaPreviewProps {
  urls: string[];
  mediaType: 'video' | 'image';
  platform?: Platform;
  className?: string;
}

/**
 * Returns aspect-ratio container config based on platform.
 * Vertical (9:16): TikTok, Instagram Reels, YouTube Shorts
 * Horizontal (16:9): Twitter, YouTube
 * Square-ish card: LinkedIn (image)
 */
function getPlatformLayout(platform?: Platform) {
  switch (platform) {
    case 'tiktok':
    case 'instagram':
    case 'youtube_shorts':
    case 'snapchat':
      return {
        orientation: 'vertical' as const,
        aspectRatio: '9 / 16',
        maxHeight: 'max-h-80',
        containerClass: 'mx-auto w-44', // phone-width
      };
    case 'twitter':
      return {
        orientation: 'horizontal' as const,
        aspectRatio: '16 / 9',
        maxHeight: 'max-h-60',
        containerClass: 'w-full',
      };
    case 'youtube':
      return {
        orientation: 'horizontal' as const,
        aspectRatio: '16 / 9',
        maxHeight: 'max-h-60',
        containerClass: 'w-full',
      };
    case 'linkedin':
      return {
        orientation: 'horizontal' as const,
        aspectRatio: '1 / 1',
        maxHeight: 'max-h-80',
        containerClass: 'w-full max-w-md mx-auto',
      };
    default:
      return {
        orientation: 'horizontal' as const,
        aspectRatio: '16 / 9',
        maxHeight: 'max-h-60',
        containerClass: 'w-full',
      };
  }
}

export function MediaPreview({ urls, mediaType, platform, className = '' }: MediaPreviewProps) {
  if (!urls || urls.length === 0) return null;

  const layout = getPlatformLayout(platform);
  const isVertical = layout.orientation === 'vertical';

  const url = urls[0];
  if (!url) return null;

  if (mediaType === 'video') {
    return (
      <div className={`${className}`}>
        {/* Phone-frame wrapper for vertical videos */}
        {isVertical ? (
          <div className="flex justify-center">
            <div
              className={`relative overflow-hidden rounded-2xl bg-gray-900 shadow-lg ${layout.containerClass} ${layout.maxHeight}`}
              style={{ aspectRatio: layout.aspectRatio }}
            >
              <video
                src={url}
                controls
                preload="metadata"
                playsInline
                className="absolute inset-0 h-full w-full object-contain"
              />
            </div>
          </div>
        ) : (
          /* Standard horizontal video */
          <div
            className={`relative overflow-hidden rounded-xl bg-gray-900 ${layout.containerClass} ${layout.maxHeight}`}
            style={{ aspectRatio: layout.aspectRatio }}
          >
            <video
              src={url}
              controls
              preload="metadata"
              playsInline
              className="absolute inset-0 h-full w-full object-contain"
            />
          </div>
        )}
      </div>
    );
  }

  // Image
  return (
    <div className={`${className}`}>
      <div
        className={`relative overflow-hidden rounded-xl bg-gray-100 ${layout.containerClass} ${layout.maxHeight}`}
        style={{ aspectRatio: layout.aspectRatio }}
      >
        <img
          src={url}
          alt="Media preview"
          className="absolute inset-0 h-full w-full object-contain"
        />
      </div>
    </div>
  );
}
